import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import Header from "@/components/cre/Header";
import Footer from "@/components/cre/Footer";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Badge } from "@/components/ui/badge";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Library, Send, X, AlertCircle, ShieldCheck } from "lucide-react";
import { Checkbox } from "@/components/ui/checkbox";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";

const ANON_NOTICE =
  "Use anonymized incident details only. Do not include customer names, real account numbers, credentials, IP addresses, internal URLs, personal data, regulated data, security secrets, or company-restricted information.";

const SEVERITY_COLORS = {
  P1: "border-rose-200 text-rose-700 bg-rose-50",
  P2: "border-amber-200 text-amber-700 bg-amber-50",
  P3: "border-sky-200 text-sky-700 bg-sky-50",
  P4: "border-slate-200 text-slate-700 bg-slate-50",
};

export default function IncidentIntake() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [samples, setSamples] = useState([]);
  const [form, setForm] = useState({
    title: "",
    description: "",
    severity: "P2",
    affected_system: "",
    symptoms: [""],
    telemetry_hints: "",
  });
  const [busy, setBusy] = useState(false);
  const [anonConfirmed, setAnonConfirmed] = useState(false);

  useEffect(() => {
    api.listSamples().then(setSamples).catch(() => {});
  }, []);

  // Auth guard — incident creation now requires sign-in.
  useEffect(() => {
    if (user === null) {
      toast.error("Please sign in to create an incident.");
      navigate("/login?next=/incidents/new");
    }
  }, [user, navigate]);

  const setField = (k, v) => setForm((f) => ({ ...f, [k]: v }));
  const setSymptom = (idx, val) =>
    setForm((f) => {
      const s = [...f.symptoms];
      s[idx] = val;
      return { ...f, symptoms: s };
    });
  const addSymptom = () =>
    setForm((f) => ({ ...f, symptoms: [...f.symptoms, ""] }));
  const removeSymptom = (idx) =>
    setForm((f) => ({
      ...f,
      symptoms: f.symptoms.filter((_, i) => i !== idx),
    }));

  const submit = async () => {
    if (!form.title.trim() || !form.description.trim() || !form.affected_system.trim()) {
      toast.error("Title, description, and affected system are required");
      return;
    }
    if (!anonConfirmed) {
      toast.error(
        "Please confirm the inputs are anonymized before continuing."
      );
      return;
    }
    setBusy(true);
    try {
      const payload = {
        ...form,
        symptoms: form.symptoms.map((s) => s.trim()).filter(Boolean),
        source: "manual",
      };
      const inc = await api.createIncident(payload);
      toast.success("Incident created");
      navigate(`/incidents/${inc.id}`);
    } catch (e) {
      const status = e?.response?.status;
      const detail = e?.response?.data?.detail;
      if (status === 401) {
        toast.error("Please sign in to create an incident.");
        navigate("/login?next=/incidents/new");
      } else {
        toast.error(
          (typeof detail === "string" && detail) ||
            detail?.message ||
            "Failed to create incident"
        );
      }
    } finally {
      setBusy(false);
    }
  };

  const loadFromSample = async (key) => {
    if (!anonConfirmed) {
      toast.error(
        "Please confirm the inputs are anonymized before continuing."
      );
      return;
    }
    setBusy(true);
    try {
      const inc = await api.createFromSample(key);
      toast.success("Incident created from sample");
      navigate(`/incidents/${inc.id}`);
    } catch (e) {
      const status = e?.response?.status;
      const detail = e?.response?.data?.detail;
      if (status === 401) {
        toast.error("Please sign in to create an incident.");
        navigate("/login?next=/incidents/new");
      } else {
        toast.error(
          (typeof detail === "string" && detail) ||
            detail?.message ||
            "Failed to load sample"
        );
      }
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="min-h-screen bg-slate-50 flex flex-col">
      <Header />
      <div className="flex-1 max-w-5xl mx-auto w-full px-5 md:px-8 py-8 md:py-10">
        <div className="cre-overline text-sky-700">Raise new incident</div>
        <h1 className="font-heading text-3xl md:text-4xl font-black tracking-tight mt-1 text-slate-900">
          Open a Reasoning Workspace
        </h1>
        <p className="mt-2 text-sm md:text-base text-slate-600 max-w-2xl">
          Start from a pre-seeded scenario or submit an anonymized incident
          manually. CRE dispatches specialist reasoning agents, builds a
          consensus view, and keeps a human-approval gate before any runbook
          action is actionable.
        </p>

        {/* Anonymization notice — shown before the form */}
        <div
          data-testid="intake-anon-notice"
          className="mt-5 max-w-3xl border border-amber-200 bg-amber-50 rounded-xl p-4 flex gap-3"
        >
          <AlertCircle
            className="h-4 w-4 text-amber-600 shrink-0 mt-0.5"
            strokeWidth={2}
          />
          <p className="text-[13px] text-slate-700 leading-relaxed">
            {ANON_NOTICE}
          </p>
        </div>

        <Tabs defaultValue="manual" className="mt-8">
          <TabsList
            className="bg-white border border-slate-200 rounded-lg p-1 font-mono"
            data-testid="intake-tabs"
          >
            <TabsTrigger value="manual" className="rounded-md data-[state=active]:bg-slate-100 data-[state=active]:text-slate-900" data-testid="tab-manual">
              Manual
            </TabsTrigger>
            <TabsTrigger value="sample" className="rounded-md data-[state=active]:bg-slate-100 data-[state=active]:text-slate-900" data-testid="tab-sample">
              Sample library
            </TabsTrigger>
          </TabsList>

          <TabsContent value="manual" className="mt-6">
            <div className="cre-card p-5 md:p-6 space-y-5" data-testid="manual-form">
              <div>
                <Label className="cre-overline">Title</Label>
                <Input
                  data-testid="intake-title"
                  placeholder="e.g. Checkout API p99 latency spiked"
                  value={form.title}
                  onChange={(e) => setField("title", e.target.value)}
                  className="mt-1.5 h-11 bg-white border-slate-200 focus-visible:ring-sky-500"
                />
              </div>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <div className="md:col-span-2">
                  <Label className="cre-overline">Affected system</Label>
                  <Input
                    data-testid="intake-system"
                    placeholder="e.g. checkout-api (prod, us-east-1)"
                    value={form.affected_system}
                    onChange={(e) => setField("affected_system", e.target.value)}
                    className="mt-1.5 h-11 bg-white border-slate-200 focus-visible:ring-sky-500"
                  />
                </div>
                <div>
                  <Label className="cre-overline">Severity</Label>
                  <Select
                    value={form.severity}
                    onValueChange={(v) => setField("severity", v)}
                  >
                    <SelectTrigger
                      data-testid="intake-severity"
                      className="mt-1.5 h-11 bg-white border-slate-200"
                    >
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {["P1", "P2", "P3", "P4"].map((s) => (
                        <SelectItem key={s} value={s}>
                          {s}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              </div>
              <div>
                <Label className="cre-overline">Description</Label>
                <Textarea
                  data-testid="intake-description"
                  rows={4}
                  placeholder="What is happening? Scope, impact, timing…"
                  value={form.description}
                  onChange={(e) => setField("description", e.target.value)}
                  className="mt-1.5 bg-white border-slate-200 focus-visible:ring-sky-500"
                />
              </div>
              <div>
                <Label className="cre-overline">Symptoms</Label>
                <div className="mt-1.5 space-y-2">
                  {form.symptoms.map((s, i) => (
                    <div key={i} className="flex gap-2">
                      <Input
                        data-testid={`intake-symptom-${i}`}
                        value={s}
                        placeholder="e.g. p99 latency rose from 220ms to 1.8s"
                        onChange={(e) => setSymptom(i, e.target.value)}
                        className="bg-white border-slate-200 focus-visible:ring-sky-500"
                      />
                      {form.symptoms.length > 1 ? (
                        <Button
                          type="button"
                          variant="outline"
                          size="icon"
                          className="rounded-md border-slate-200"
                          onClick={() => removeSymptom(i)}
                          data-testid={`intake-remove-symptom-${i}`}
                        >
                          <X className="h-4 w-4" />
                        </Button>
                      ) : null}
                    </div>
                  ))}
                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    onClick={addSymptom}
                    data-testid="intake-add-symptom"
                    className="rounded-md border-slate-200 text-slate-700"
                  >
                    + Add symptom
                  </Button>
                </div>
              </div>
              <div>
                <Label className="cre-overline">Telemetry hints (optional)</Label>
                <Textarea
                  data-testid="intake-telemetry"
                  rows={3}
                  placeholder="Recent deploys, scaling events, infra changes, correlations…"
                  value={form.telemetry_hints}
                  onChange={(e) => setField("telemetry_hints", e.target.value)}
                  className="mt-1.5 bg-white border-slate-200 focus-visible:ring-sky-500 font-mono text-sm"
                />
              </div>
              <div className="border-t border-slate-200 pt-4">
                <label
                  data-testid="anon-checkbox-label"
                  htmlFor="anon-confirm"
                  className="flex items-start gap-3 cursor-pointer select-none"
                >
                  <Checkbox
                    id="anon-confirm"
                    data-testid="anon-checkbox"
                    checked={anonConfirmed}
                    onCheckedChange={(v) => setAnonConfirmed(Boolean(v))}
                    className="mt-0.5 border-slate-300 data-[state=checked]:bg-sky-600 data-[state=checked]:border-sky-600"
                  />
                  <span className="text-[13px] text-slate-700 leading-relaxed">
                    I confirm that the information entered is anonymized and
                    does not include confidential, personal, regulated,
                    production-sensitive, security-sensitive, or
                    company-restricted data.
                  </span>
                </label>
              </div>
              <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 pt-2">
                <div className="inline-flex items-center gap-2 text-[12px] text-slate-500">
                  <ShieldCheck className="h-3.5 w-3.5 text-emerald-600" strokeWidth={2} />
                  Advisory Mode — Human Approval Required
                </div>
                <Button
                  disabled={busy || !anonConfirmed}
                  onClick={submit}
                  data-testid="intake-submit"
                  className="cre-cta-primary rounded-lg font-semibold h-11 disabled:opacity-60 disabled:cursor-not-allowed"
                >
                  <Send className="h-4 w-4 mr-2" strokeWidth={2} /> Raise incident
                </Button>
              </div>
            </div>
          </TabsContent>

          <TabsContent value="sample" className="mt-6">
            <div className="cre-card p-5 md:p-6" data-testid="sample-form">
              <div className="flex items-center gap-2 mb-3">
                <Library className="h-4 w-4 text-emerald-600" strokeWidth={1.75} />
                <h2 className="font-heading text-lg font-bold tracking-tight text-slate-900">
                  Pick a scenario
                </h2>
              </div>
              <label
                htmlFor="anon-confirm-sample"
                className="flex items-start gap-3 cursor-pointer select-none mb-4 border border-slate-200 rounded-lg p-3 bg-slate-50"
              >
                <Checkbox
                  id="anon-confirm-sample"
                  data-testid="anon-checkbox-sample"
                  checked={anonConfirmed}
                  onCheckedChange={(v) => setAnonConfirmed(Boolean(v))}
                  className="mt-0.5 border-slate-300 data-[state=checked]:bg-sky-600 data-[state=checked]:border-sky-600"
                />
                <span className="text-[13px] text-slate-700 leading-relaxed">
                  I confirm that the information entered is anonymized and
                  does not include confidential, personal, regulated,
                  production-sensitive, security-sensitive, or
                  company-restricted data.
                </span>
              </label>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                {samples.map((s) => (
                  <div
                    key={s.key}
                    className="border border-slate-200 rounded-xl p-4 hover:border-sky-300 hover:shadow-sm transition-all bg-white"
                    data-testid={`sample-card-${s.key}`}
                  >
                    <div className="flex items-center gap-2">
                      <Badge
                        variant="outline"
                        className={`rounded-md font-mono text-[10px] ${SEVERITY_COLORS[s.severity] || ""}`}
                      >
                        {s.severity}
                      </Badge>
                      <span className="text-sm text-slate-900 font-medium">
                        {s.title}
                      </span>
                    </div>
                    <p className="mt-2 text-xs text-slate-600 leading-relaxed line-clamp-3">
                      {s.description}
                    </p>
                    <div className="mt-3 flex items-center justify-between gap-2">
                      <span className="text-[11px] font-mono text-slate-500 truncate">
                        {s.affected_system}
                      </span>
                      <Button
                        size="sm"
                        disabled={busy || !anonConfirmed}
                        onClick={() => loadFromSample(s.key)}
                        data-testid={`load-sample-${s.key}`}
                        className="cre-cta-primary rounded-md disabled:opacity-60 shrink-0"
                      >
                        Load
                      </Button>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </TabsContent>
        </Tabs>
      </div>
      <Footer />
    </div>
  );
}
